import React, { useState, useEffect } from 'react';
import { lessonSummaryTurtle, lessonSummarySplash, lessonSummaryStar, lessonSummaryShark, lessonSummaryFinalSplash } from '../../assets/images';
import { useLessonSummary } from './useLessonSummary';
import { playIntroFanfare, resetSoundEngine } from './utils/soundEngine';
import Lottie from 'lottie-react';
import celebrationAnimation from '../../assets/animations/celebration.json';
import './lesson-summary.scss';

export default function LessonSummary() {
  const {
    t,
    loading,
    pointsLoading,
    stats,
    fullTopic,
    levelName,
    isFinalSummary,
    percentile,
    percentileLoading,
    learningTime,
    handleNavigate,
    ROUTERS_PATH,
    exerciseList
  } = useLessonSummary();

  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    // Reset sound engine and play Web Audio victory fanfare automatically on mount
    resetSoundEngine();
    playIntroFanfare();

    // Turned off Lottie celebration overlay per user request
    // if (isFinalSummary) {
    //   setShowCelebration(true);
    // }
  }, [isFinalSummary]);

  const mainTitleText = isFinalSummary
    ? (exerciseList.length > 0 ? t('topicLearningEnded') : t('lessonEnded'))
    : t('lessonPhaseSummary');

  let subtitleText = '';
  if (fullTopic) {
    if (fullTopic.startsWith('תרגילים')) {
      subtitleText = fullTopic;
    } else if (levelName) {
      subtitleText = t('lessonSummarySubtitle', { level: levelName, topic: fullTopic });
    } else {
      subtitleText = t('lessonSummarySubtitleTopicOnly', { topic: fullTopic });
    }
  } else if (levelName) {
    subtitleText = levelName;
  }

  const bannerText = fullTopic
    ? (isFinalSummary
        ? t('lessonSummaryGreetingFinal', { topic: fullTopic })
        : t('lessonSummaryGreeting', { topic: fullTopic }))
    : t('lessonSummaryGreetingNoTopic');

  return (
    <div className={`lesson-summary-page fade-in ${isFinalSummary ? 'lesson-summary-page--final' : ''}`}>
      {showCelebration && (
        <div className="lesson-summary-page__celebration-overlay">
          <Lottie
            animationData={celebrationAnimation}
            loop={false}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
            onComplete={() => setShowCelebration(false)}
          />
        </div>
      )}

      <div className="scroll">
        {/* Rising Bubbles Background Layer */}
        <div className="bubbles" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className="bub" />
          ))}
        </div>

        <div className="content">
          {/* Hero Ocean Animated Section */}
          <div className="hero">
            {isFinalSummary ? (
              <img className="splash-burst" src={lessonSummaryFinalSplash} alt="" />
            ) : (
              <img className="splash" src={lessonSummarySplash} alt="" />
            )}
            <div className="current">
              <span className="current-line" />
              <span className="current-line" />
              <span className="current-line" />
            </div>

            {/* Fish School Swimming Across Ocean */}
            <div className="fish-school" aria-hidden="true">
              {Array.from({ length: 10 }).map((_, i) => (
                <span key={i} className="fish" />
              ))}
            </div>

            {isFinalSummary ? (
              <img className="shark" src={lessonSummaryShark} alt="Shark" />
            ) : (
              <img className="turtle-solo" src={lessonSummaryTurtle} alt="Sea Turtle" />
            )}
          </div>

          <h1 className="title">{mainTitleText}</h1>
          {subtitleText && <div className="subtitle">{subtitleText}</div>}

          {/* Animated Stat Cards Grid with Playwright locators compatibility */}
          <div className="grid lesson-summary-page__stats-grid">
            <div className="stat summary-stats-card">
              <div className="v summary-stats-card__value">
                {pointsLoading ? '...' : `${stats.pointsEarned}+`}
              </div>
              <div className="l summary-stats-card__label">{t('pointsEarnedText')}</div>
            </div>

            <div className="stat summary-stats-card">
              <div className="v summary-stats-card__value">
                {percentileLoading ? '...' : `אחוזון ${percentile || 66}`}
              </div>
              <div className="l summary-stats-card__label">{t('percentileSubtext')}</div>
            </div>

            <div className="stat summary-stats-card">
              <div className="v summary-stats-card__value">
                {loading ? '...' : learningTime}
              </div>
              <div className="l summary-stats-card__label">{t('learningTime')}</div>
            </div>

            <div className="stat summary-stats-card">
              <div className="v summary-stats-card__value">
                {loading ? '...' : `${stats.accuracy}%`}
              </div>
              <div className="l summary-stats-card__label">{t('accuracySubtext')}</div>
            </div>
          </div>

          {/* Shimmering Success Banner */}
          <div className="banner-wrap fade-in">
            <div className="banner">
              <img className="spark" src={lessonSummaryStar} alt="Star Sparkle" />
              {bannerText}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Action Buttons */}
      <div className="footer lesson-summary-page__actions">
        {isFinalSummary && exerciseList.length > 0 ? (
          <>
            <button
              className="btn btn-ghost"
              onClick={() => handleNavigate(ROUTERS_PATH.SEARCH_LESSON, true)}
            >
              {t('learnAnotherTopic')}
            </button>
            <button
              className="btn btn-dark"
              onClick={() => handleNavigate(ROUTERS_PATH.SEARCH_LESSON)}
            >
              {t('topicCompletionPractice')}
            </button>
          </>
        ) : (
          <>
            <button
              className="btn btn-ghost"
              onClick={() => handleNavigate(ROUTERS_PATH.USER_MAIN_DASHBOARD)}
            >
              {t('exit')}
            </button>
            <button
              className="btn btn-dark"
              onClick={() => handleNavigate(isFinalSummary ? ROUTERS_PATH.SEARCH_LESSON : ROUTERS_PATH.PLAY_LESSON)}
            >
              {isFinalSummary ? t('additionalLesson') : t('lessonContinue')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
